import React from 'react';
import { SortingModelTrigger, useSortingModel } from './index';
import styled from 'styled-components';
import { toast } from 'react-toastify';

const ExampleContainer = styled.div`
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
`;

const Title = styled.h1`
  margin: 0;
  color: #333;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const StatCard = styled.div`
  padding: 16px;
  background: white;
  border: 1px solid #eee;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const StatTitle = styled.h3`
  margin: 0 0 8px 0;
  color: #666;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #333;
`;

const RankingsContainer = styled.div`
  background: white;
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden;
`;

const RankingsHeader = styled.div`
  padding: 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const RankingsTitle = styled.h2`
  margin: 0;
  color: #333;
`;

const RankingsList = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const RankingItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: #f8f9fa;
  }
`;

const HospitalInfo = styled.div`
  flex: 1;
`;

const HospitalName = styled.div`
  font-weight: 500;
  color: #333;
`;

const HospitalProvider = styled.div`
  font-size: 12px;
  color: #666;
`;

const RankingScore = styled.div`
  text-align: right;
`;

const Rank = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: #333;
`;

const Score = styled.div`
  font-size: 12px;
  color: #666;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #dc3545;
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 8px;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const SortingModelExample = () => {
  const {
    isLoading,
    error,
    isSuccess,
    weightages,
    rankings,
    totalHospitalsRanked,
    sampleRankings,
    createModel,
    resetModel,
    clearCache,
    getCacheInformation
  } = useSortingModel();

  const handleQuickTest = async () => {
    const testWeightages = {
      beds: 25,
      discharges: 30,
      revenue: 20,
      rating: 25
    };

    const result = await createModel(testWeightages);
    if (result.success) {
      console.log('Quick test completed successfully!');
    }
  };

  const handleClearCache = async () => {
    const result = await clearCache();
    if (result.success) {
      toast.success('Cache cleared successfully!');
    } else {
      toast.error(`Failed to clear cache: ${result.error}`);
    }
  };

  const handleGetCacheInfo = async () => {
    const result = await getCacheInformation();
    if (result.success) {
      console.log('Cache info:', result.info);
      toast.info('Cache info logged to console');
    } else {
      toast.error(`Failed to get cache info: ${result.error}`);
    }
  };

  const displayRankings = rankings.length > 0 ? rankings : [];

  return (
    <ExampleContainer>
      <Header>
        <Title>Hospital Sorting Model Example</Title>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <SortingModelTrigger buttonText="Open Sorting Model" />
          <button 
            onClick={handleQuickTest}
            disabled={isLoading}
            style={{
              padding: '8px 16px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.6 : 1
            }}
          >
            {isLoading ? 'Running...' : 'Quick Test'}
          </button>
          <button 
            onClick={handleClearCache}
            style={{
              padding: '8px 16px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Clear Cache
          </button>
          <button 
            onClick={handleGetCacheInfo}
            style={{
              padding: '8px 16px',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Cache Info
          </button>
          <button 
            onClick={resetModel}
            style={{
              padding: '8px 16px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Reset
          </button>
        </div>
      </Header>

      <StatsContainer>
        <StatCard>
          <StatTitle>Total Hospitals Ranked</StatTitle>
          <StatValue>{totalHospitalsRanked || 0}</StatValue>
        </StatCard>
        <StatCard>
          <StatTitle>Current Weightages</StatTitle>
          <StatValue>
            {Object.entries(weightages).map(([key, value]) => (
              <div key={key} style={{ fontSize: '14px', marginBottom: '4px' }}>
                {key}: {value}
              </div>
            ))}
          </StatValue>
        </StatCard>
        <StatCard>
          <StatTitle>Status</StatTitle>
          <StatValue>
            {isLoading ? 'Loading...' : 
             error ? 'Error' : 
             isSuccess ? 'Success' : 'Ready'}
          </StatValue>
        </StatCard>
      </StatsContainer>

      <RankingsContainer>
        <RankingsHeader>
          <RankingsTitle>Hospital Rankings</RankingsTitle>
          <div style={{ fontSize: '14px', color: '#666' }}>
            {displayRankings.length} hospitals shown
          </div>
        </RankingsHeader>

        <RankingsList>
          {isLoading && (
            <LoadingMessage>
              Loading hospital rankings...
            </LoadingMessage>
          )}

          {error && (
            <ErrorMessage>
              Error loading rankings: {error}
            </ErrorMessage>
          )}

          {!isLoading && !error && displayRankings.length === 0 && (
            <EmptyMessage>
              No rankings available. Use the sorting model to create rankings.
            </EmptyMessage>
          )}

          {!isLoading && !error && displayRankings.length > 0 && (
            displayRankings.map((hospital, index) => (
              <RankingItem key={hospital.provider_number || index}>
                <HospitalInfo>
                  <HospitalName>{hospital.name}</HospitalName>
                  <HospitalProvider>
                    Provider: {hospital.provider_number}
                  </HospitalProvider>
                </HospitalInfo>
                <RankingScore>
                  <Rank>#{hospital.ranking || index + 1}</Rank>
                  <Score>Score: {hospital.score?.toFixed(4) || 'N/A'}</Score>
                </RankingScore>
              </RankingItem>
            ))
          )}
        </RankingsList>
      </RankingsContainer>
    </ExampleContainer>
  );
};

export default SortingModelExample;
